import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import AnnotationTable from 'molbio/annotations/AnnotationTable';
import AnnotationTypeVisibilityDropdown from 'molbio/annotations/AnnotationTypeVisibilityDropdown';
import {AnnotationData, getUniqueNonEmptyAnnotationTypes} from 'molbio/annotations/AnnotationUtil';
import {FONT_PROXIMA_19} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';

import AnnotationBulkEditModal, {BulkEditAnnotationProperties} from './AnnotationBulkEditModal';

export interface ExpandedAnnotationTabProps {
  annotations: Array<AnnotationData>;
  allowFeatureEdits: boolean;
  saveAnnotationsAsFeatures: (cids: Array<string>) => void;
  // Callback to run CRISPR on the selected annotations, or null if CRISPR is not supported.
  runCrispr: ((cids: Array<string>) => void) | null;
  deleteAnnotations: (cids: Array<string>) => void;
  bulkEditAnnotations: (cids: Array<string>, updatedPropertie: BulkEditAnnotationProperties) => void;
  crisprInProgress: boolean;
  isSingleStranded: boolean;
  onToggleVisibility: (cids: Array<string>, visibility: boolean) => void;
}

/**
 * Renders the expanded annotations tab view.
 *
 * This includes a selectable table of annotations and some bulk actions that can be taken on those annotations.
 * @param annotations List of annotations to display.
 * @param saveAnnotationsAsFeatures Handles saving selected annotations to feature library
 * @param runCrispr Handles designing CRISPR guides for selected annotations.
 * @param deleteAnnotations Handles bulk deleting selected annotations.
 */
export default function ExpandedAnnotationTab({
  annotations,
  allowFeatureEdits,
  saveAnnotationsAsFeatures,
  runCrispr,
  deleteAnnotations,
  bulkEditAnnotations,
  crisprInProgress,
  isSingleStranded,
  onToggleVisibility,
}: ExpandedAnnotationTabProps): JSX.Element {
  const crisprTooltipRef = useRef<Tooltip | null>(null);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const annotationsByCid = groupByUniqueKey(annotations, (annotation) => annotation.cid);
  // If sequence features are editable, any uneditable annotations are hoisted from parts
  const selectedPartAnnotationIds = selectedIds.filter((id) => !annotationsByCid[id]?.editable);
  const hiddenAnnotationsCount = annotations.filter(({visible}) => !visible).length;

  useEffect(() => {
    if (!allowFeatureEdits) {
      setSelectedIds([]);
    }
  }, [allowFeatureEdits]);

  return (
    <div
      className={css(styles.tabContainer)}
      {...getTestProps({component: 'ExpandedAnnotationTab', element: 'root'})}
    >
      <div className={css(styles.tabHeaderContainer)}>
        <p className={css(styles.tabHeader)}>Annotations</p>
        <p className={css(styles.tabSubheader)}>{`${annotations.length} total${
          hiddenAnnotationsCount > 0 ? `, ${hiddenAnnotationsCount} hidden` : ''
        }`}</p>
      </div>
      <div className={css(styles.actionsContainer)}>
        <ButtonGroup>
          <AnnotationTypeVisibilityDropdown
            annotations={annotations}
            onToggleVisibility={onToggleVisibility}
          />
        </ButtonGroup>
        <div className={css(styles.buttonsContainer)}>
          {selectedPartAnnotationIds.length > 0 && (
            <div className={css(styles.partsInfoContainer)}>
              <p className={css(styles.partsInfoText)}>{`${selectedPartAnnotationIds.length} ${pluralize(
                selectedPartAnnotationIds.length,
                'part annotation'
              )} selected`}</p>
              <Tooltip title="Cannot edit or delete annotations that are inside parts">
                <BenchlingIcon icon="info-circle" />
              </Tooltip>
            </div>
          )}
          <ButtonGroup>
            <Tooltip title="Edit">
              <Button
                icon="edit"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  runModal(AnnotationBulkEditModal, {
                    selectedAnnotations: selectedIds.map((id) => annotationsByCid[id]),
                    isSingleStranded,
                    allAnnotationTypes: getUniqueNonEmptyAnnotationTypes(annotations),
                    bulkEditAnnotations: (updatedProperties: BulkEditAnnotationProperties) => {
                      bulkEditAnnotations(selectedIds, updatedProperties);
                    },
                  });
                }}
                disabled={
                  !allowFeatureEdits || selectedIds.length === 0 || selectedPartAnnotationIds.length > 0
                }
              />
            </Tooltip>
            <Tooltip title="Save to feature library">
              <Button
                icon="save"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  saveAnnotationsAsFeatures(selectedIds);
                }}
                disabled={selectedIds.length === 0}
              />
            </Tooltip>
            {runCrispr && (
              <Tooltip title="Design CRISPR guides" ref={crisprTooltipRef}>
                <Button
                  icon="crispr"
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    runCrispr(selectedIds);
                    // Force the CRISPR button tooltip to close before opening design CRISPR tab
                    crisprTooltipRef.current?.$tooltip('hide');
                  }}
                  disabled={!allowFeatureEdits || selectedIds.length === 0 || crisprInProgress}
                />
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <Button
                icon="delete"
                buttonStyle={ButtonStyle.NEGATIVE}
                size={ButtonSize.SMALL}
                onClick={() => {
                  deleteAnnotations(selectedIds);
                  setSelectedIds([]);
                }}
                disabled={
                  !allowFeatureEdits || selectedIds.length === 0 || selectedPartAnnotationIds.length > 0
                }
              />
            </Tooltip>
          </ButtonGroup>
        </div>
      </div>
      <AnnotationTable
        annotations={annotations}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        allowFeatureEdits={allowFeatureEdits}
        onToggleVisibility={onToggleVisibility}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  tabContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabHeaderContainer: {
    display: 'flex',
    alignItems: 'baseline',
  },
  tabHeader: FONT_PROXIMA_19,
  tabSubheader: {
    color: GRAY_DARKER,
    marginLeft: '10px',
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  buttonsContainer: {
    display: 'flex',
  },
  partsInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
    color: GRAY_DARKER,
  },
  partsInfoText: {
    marginBottom: '0',
    marginRight: '4px',
  },
});
